import type { GexLevels, OptionRow } from '../services/gexCalculator';
import type { PinAnalytics } from '../types/marketAnalytics';

/** Pin / expiration analytics from OI + volume clustering (module 4). */
export function buildPinAnalytics(
  levels: GexLevels | null,
  rows: OptionRow[],
  spot: number,
  minutesToClose: number,
): PinAnalytics {
  const empty: PinAnalytics = {
    gravity_strike: null,
    gravity_score: null,
    pin_defense_active: false,
    volume_clusters: [],
  };
  if (!levels?.strikes?.length || spot <= 0) return empty;

  const volByStrike = new Map<number, number>();
  for (const row of rows) {
    const k = Number(row.Strike ?? row.strike ?? 0);
    const vol = Number(row.Volume ?? row.volume ?? 0) || 0;
    if (!Number.isFinite(k) || vol <= 0) continue;
    volByStrike.set(k, (volByStrike.get(k) ?? 0) + vol);
  }

  const clusters = Array.from(volByStrike.entries())
    .map(([strike, volume]) => ({ strike, volume }))
    .sort((a, b) => b.volume - a.volume)
    .slice(0, 8);

  const top = levels.strikes.reduce((best, s) => {
    const oi = s.callOI + s.putOI;
    const bestOi = best.callOI + best.putOI;
    return oi > bestOi ? s : best;
  }, levels.strikes[0]);

  const totalOi = levels.strikes.reduce((s, k) => s + k.callOI + k.putOI, 0);
  const gravity_score = totalOi > 0 ? (top.callOI + top.putOI) / totalOi : null;

  const nearWallVol = clusters
    .filter((c) => Math.abs(c.strike - spot) / spot < 0.02)
    .reduce((s, c) => s + c.volume, 0);
  const totalVol = clusters.reduce((s, c) => s + c.volume, 0);
  const pin_defense_active = Boolean(
    minutesToClose < 120 && totalVol > 0 && nearWallVol / totalVol > 0.35,
  );

  return {
    gravity_strike: top?.strike ?? null,
    gravity_score,
    pin_defense_active,
    volume_clusters: clusters,
  };
}
